import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { Loader2, Flag, ShieldAlert } from "lucide-react";
import { PostComposer } from "./PostComposer";
import { PostCard } from "./PostCard";
import { ConfirmDialog } from "./ConfirmDialog";
import { FLOOR_POST_CREATED_EVENT } from "./PortalCreateLauncher";

// Nav Cleanup, Stage 2B (2026-11): "Feedback requests" added as a third sort filter.
const SORT_FILTERS = ["Latest", "Featured", "Feedback requests"];
// Same tier as kernel/permissions.py's NJM_ONLY_ROLES — every one of these roles holds
// community.moderate; freelancer/production_partner never do.
const MODERATOR_ROLES = ["founder", "partner", "pm", "coordinator", "finance", "creative"];
const CLEAR_ALL_PHRASE = "DELETE ALL COMMUNITY DATA";

// Freelancer Portal Upgrade Phase 3/4 (2026-02) — Community tab. Private to logged-in
// approved members only: every session reaching this component already passed
// requires_scope("community.read"/"write") server-side, which access_resolver.py only ever
// grants to an APPROVED, portal_access freelancer or NJM staff. Rendered from TWO entry
// points — the Freelancer Portal's own tab AND the staff-facing /community page (AppShell) —
// both hit the exact same GET /v1/community/posts, i.e. ONE shared feed, never per-user.
export function CommunityTab({ deepLinkPostId, clearDeepLink, portalComposer = false } = {}) {
  const { user } = useAuth();
  const isModerator = MODERATOR_ROLES.includes(user?.role);
  const isFounder = user?.role === "founder";
  const [posts, setPosts] = useState(null);
  const [members, setMembers] = useState([]);
  const [sortFilter, setSortFilter] = useState("Latest");
  const [roleFilter, setRoleFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [reports, setReports] = useState([]);
  const [showReports, setShowReports] = useState(false);
  const [composerExpanded, setComposerExpanded] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [playingVideoPostId, setPlayingVideoPostId] = useState(null);
  const filterRowRef = useRef(null);

  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedError, setFeedError] = useState(false);
  const generation = useRef(0);
  const pageInFlight = useRef(false);
  const focusPost = useRef(deepLinkPostId);
  if (deepLinkPostId) focusPost.current = deepLinkPostId;

  const loadPosts = useCallback((filter, opts = {}) => {
    if (opts.append && pageInFlight.current) return;
    const request = opts.append ? generation.current : ++generation.current;
    pageInFlight.current = true;
    setLoadingMore(!!opts.append); setFeedError(false);
    if (!opts.append) { setNextCursor(null); if (!opts.silent) setPosts(null); }
    api.get("/v1/community/posts", { params: {
      featured_only: filter === "Featured", feedback_requests_only: filter === "Feedback requests",
      page_size: 20, cursor: opts.cursor || undefined, role: roleFilter || undefined,
      city: cityFilter || undefined, exclude_collaborations: true, focus_post_id: focusPost.current || undefined,
    } }).then((r) => {
      if (request !== generation.current) return;
      const incoming = [r.data.focused_post, ...(r.data.items || [])].filter((p) => p && p.post_type !== "collaboration");
      setPosts((previous) => [...new Map([...(opts.append ? previous || [] : []), ...incoming].map((p) => [p.id, p])).values()]);
      setNextCursor(r.data.next_cursor || null);
    }).catch(() => {
      if (request !== generation.current) return;
      setFeedError(true); setPosts((previous) => previous || []);
    }).finally(() => {
      if (request === generation.current) { pageInFlight.current = false; setLoadingMore(false); }
    });
  }, [roleFilter, cityFilter]);

  const loadReports = () => {
    if (!isModerator) return;
    api.get("/v1/community/reports").then((r) => setReports(r.data.items)).catch(() => {});
  };

  useEffect(() => {
    loadPosts(sortFilter);
    return () => { generation.current += 1; pageInFlight.current = false; };
  }, [sortFilter, loadPosts, deepLinkPostId]);
  useEffect(() => {
    api.get("/v1/community/members").then((r) => setMembers(r.data.items)).catch(() => {});
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nav Cleanup, Stage 2B (2026-11): keeps the active filter pill in view when the row
  // scrolls horizontally on mobile (the row itself just overflow-x-auto's below).
  useEffect(() => {
    filterRowRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
  }, [sortFilter]);

  const refresh = () => { loadPosts(sortFilter, { silent: true }); loadReports(); };
  // Portal-wide "Create" launcher (2026-10 staging): a post/collaboration created from the
  // floating + on any tab announces itself so an open Floor feed refreshes silently.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    const onCreated = () => refreshRef.current();
    window.addEventListener(FLOOR_POST_CREATED_EVENT, onCreated);
    return () => window.removeEventListener(FLOOR_POST_CREATED_EVENT, onCreated);
  }, []);

  // Notification deep-links (2026-08, founder-requested: "take the freelancer to the specific
  // job/item... not the generic tab"): a notification whose `link.postId` points here scrolls
  // straight to that post and auto-expands its comments, instead of just landing on The Floor
  // and leaving the member to scroll and find it. iteration_187 finding: an active role/city/
  // Collaborations filter could hide the deep-linked post from `visible` entirely (no scroll,
  // no feedback) — cleared here so the target post is guaranteed to actually render. Matches
  // BOTH a regular post's testid and a Creative Radar card's own `radar-post-{id}` testid
  // (radar cards render a different component with no comment thread to auto-open).
  useEffect(() => {
    if (!deepLinkPostId || !posts?.length) return;
    const target = posts.find((p) => p.id === deepLinkPostId);
    if (!target) return;
    if (roleFilter && target.author_primary_role !== roleFilter) setRoleFilter("");
    if (cityFilter && target.author_city !== cityFilter) setCityFilter("");
    const timer = setTimeout(() => {
      document.querySelector(`[data-testid="community-post-${deepLinkPostId}"], [data-testid="radar-post-${deepLinkPostId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => clearTimeout(timer);
  }, [deepLinkPostId, posts, roleFilter, cityFilter]);

  const clearAllCommunityData = async () => {
    setClearing(true);
    try {
      const r = await api.post("/v1/community/admin/clear-all", { confirm: CLEAR_ALL_PHRASE });
      const d = r.data.deleted;
      toast.success(`Cleared ${d.posts} posts, ${d.comments} comments, ${d.likes} likes, ${d.drive_files} files`);
      refresh();
    } catch {
      toast.error("Couldn't clear community data");
    } finally {
      setClearing(false);
    }
  };

  const roles = [...new Set(members.map((m) => m.primary_role).filter(Boolean))].sort();
  const cities = [...new Set(members.map((m) => m.city).filter(Boolean))].sort();

  // Design pass (2026-02): role/city are now persistent dropdown filters (applied alongside
  // Latest/Featured, not a separate mutually-exclusive tab) — matches the founder's updated
  // reference. Same underlying data/fields (author_primary_role/author_city), just always on.
  // Creator of the Week design conformance pass (2026-11): the pinned Creator of the Week post
  // has no author_primary_role/author_city of its own (its "author" is NJM staff, not the
  // featured creator) and must be visible to every member during its 7 pinned days regardless
  // of whichever role/city filter they happen to have selected.
  const visible = (posts || []).filter((p) => {
    if (p.post_type === "creator_of_week") return true;
    if (roleFilter && p.author_primary_role !== roleFilter) return false;
    if (cityFilter && p.author_city !== cityFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="community-tab">
      <div>
        <h2 data-testid="community-floor-heading">
          <span className="inline-block bg-brand text-ink font-editorial uppercase tracking-tight text-2xl sm:text-3xl px-3.5 py-1.5 rounded-md">
            The Floor
          </span>
        </h2>
        <p className="mt-2.5 text-sm text-ink-soft">Recent work and studio notes, shared between NJM members only.</p>
      </div>

      {!portalComposer && <PostComposer members={members} onPosted={refresh} expanded={composerExpanded} onExpandedChange={setComposerExpanded} />}

      {isModerator && (
        <div data-testid="community-reports-section">
          <button onClick={() => setShowReports((s) => !s)} data-testid="community-reports-toggle"
            className="inline-flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink transition-colors">
            <Flag className="h-3.5 w-3.5" /> Reports{reports.length > 0 ? ` (${reports.length})` : ""}
          </button>
          {showReports && (
            <div className="mt-2 rounded-lg border border-hairline divide-y divide-hairline overflow-hidden" data-testid="community-reports-panel">
              {reports.length === 0 ? (
                <div className="text-xs text-faint px-3 py-3" data-testid="community-reports-empty">No open reports.</div>
              ) : reports.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs" data-testid={`community-report-${r.id}`}>
                  <div className="min-w-0 text-ink-soft truncate">
                    <span className="text-ink font-medium">{r.reporter_name}</span> flagged a {r.target_type}{r.reason ? `: "${r.reason}"` : ""}
                  </div>
                  <button onClick={async () => { await api.post(`/v1/community/reports/${r.id}/resolve`); loadReports(); }}
                    data-testid={`community-report-resolve-${r.id}`} className="text-faint hover:text-ink shrink-0">Resolve</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 overflow-x-auto sm:flex-wrap" style={{ scrollbarWidth: "none" }} ref={filterRowRef} data-testid="community-filters">
        {SORT_FILTERS.map((f) => (
          <button key={f} onClick={() => setSortFilter(f)} data-testid={`community-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            data-active={sortFilter === f}
            className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${sortFilter === f ? "bg-brand text-ink" : "border border-hairline text-ink-soft hover:border-brand"}`}>
            {f}
          </button>
        ))}
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="community-role-filter-select"
          className="shrink-0 rounded-full border border-hairline bg-paper px-3 py-1.5 text-xs text-ink-soft focus:outline-none focus:border-brand/50 cursor-pointer">
          <option value="">All roles</option>
          {roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} data-testid="community-city-filter-select"
          className="shrink-0 rounded-full border border-hairline bg-paper px-3 py-1.5 text-xs text-ink-soft focus:outline-none focus:border-brand/50 cursor-pointer">
          <option value="">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {posts === null ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-faint" /></div>
      ) : posts.length === 0 ? (
        // Inviting first-post UX (2026-02): a genuinely empty Floor (no posts at all yet, not
        // just filtered to zero) gets a warm, specific invitation with a real action straight
        // into the composer — not a generic "nothing here" box.
        <EmptyState
          title="Be the first to post on The Floor"
          hint="Share a BTS shot, recent work, or a tip for the crew — it shows up here for every NJM member."
          testId="community-feed-empty"
          actionLabel="Share your first post"
          onAction={() => portalComposer ? window.dispatchEvent(new CustomEvent("njm:open-floor-composer")) : setComposerExpanded(true)}
        />
      ) : visible.length === 0 ? (
        <EmptyState title="No posts match these filters" hint="Try clearing a filter above." testId="community-feed-filtered-empty" />
      ) : (
        <div className="space-y-4" data-testid="community-feed">
          {visible.map((p) => <PostCard key={p.id} post={p} members={members} isModerator={isModerator} viewerId={user?.id} viewerIsFreelancer={user?.role === "freelancer"} onChanged={refresh} autoOpenComments={p.id === deepLinkPostId} onAutoOpened={clearDeepLink} isVideoPlaying={playingVideoPostId === p.id} onPlayVideo={() => setPlayingVideoPostId(p.id)} />)}
        </div>
      )}

      {feedError && <div role="alert" className="text-sm text-ink-soft">Couldn't load posts. <button type="button" className="min-h-[44px] underline px-2" onClick={() => loadPosts(sortFilter, { append: !!nextCursor, cursor: nextCursor })}>Retry</button></div>}
      {nextCursor && <button type="button" disabled={loadingMore} data-testid="community-load-more" className="min-h-[44px] w-full rounded-lg border border-hairline px-4 py-3 text-sm" onClick={() => loadPosts(sortFilter, { append: true, cursor: nextCursor })}>{loadingMore ? "Loading…" : "Load more posts"}</button>}

      {isFounder && (
        <div className="mt-10 rounded-lg border border-red-200 bg-red-50/40 p-4" data-testid="community-danger-zone">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-red-700"><ShieldAlert className="h-3.5 w-3.5" /> Founder-only</div>
          <p className="mt-1 text-xs text-red-700/80 max-w-md">
            Permanently deletes every post, comment, like, and uploaded image on The Floor. Never touches freelancer profiles, accounts, or anything outside Community. This cannot be undone.
          </p>
          <ConfirmDialog
            testIdPrefix="community-clear-all"
            title="Clear all community posts?"
            description={`This permanently deletes ALL Floor posts, comments, likes, and images — for every member. There is no undo. Type "${CLEAR_ALL_PHRASE}" to confirm.`}
            confirmLabel="Delete everything"
            requirePhrase={CLEAR_ALL_PHRASE}
            onConfirm={clearAllCommunityData}
            trigger={
              <button disabled={clearing} data-testid="community-clear-all-btn"
                className="mt-3 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50">
                {clearing ? "Clearing…" : "Clear all community posts"}
              </button>
            }
          />
        </div>
      )}
    </div>
  );
}
